"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { iconRegistry } from "@pcln/horizon-icons";
import { forwardRef, useRef } from "react";
import { cleanProps, cn } from "../../utils/index.js";
import { getIconSize } from "../../variants/interactions/iconButtonVariants.utils.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { HorizonIcon } from "../HorizonIcon/index.js";
import { Spinner } from "../Spinner/index.js";
import { iconButtonVariants } from "./IconButton.variants.js";
const pressStartDedupeMs = 16;
const compatibilityMouseDedupeMs = 500;
const IconButton_IconButton = /*#__PURE__*/ forwardRef(function({ onClick, onPointerDown, fillNone = false, loading = false, className, iconName, title, slots = {
    buttonSlot: '',
    wrapperSlot: '',
    iconSlot: '',
    tapSpaceSlot: ''
}, buttonClassName, ...props }, ref) {
    const lastPressStartRef = useRef(null);
    const { buttonSlot, wrapperSlot, iconSlot, tapSpaceSlot } = iconButtonVariants({
        ...props,
        fillNone,
        disabled: props.disabled || loading
    });
    const wrapperClasses = cn(wrapperSlot(), className, slots.wrapperSlot);
    const iconSlotClasses = cn(iconSlot(), slots.iconSlot);
    const buttonClasses = cn(buttonSlot(), buttonClassName, slots.buttonSlot);
    const tapSpaceClasses = cn(tapSpaceSlot(), slots.tapSpaceSlot);
    const iconSize = getIconSize(props.size);
    const spinnerSize = 'lg' === props.size || 'md' === props.size ? 'sm' : 'xs';
    const isIconName = (name)=>'string' == typeof name && Object.hasOwn(iconRegistry, name);
    let iconContent;
    iconContent = loading ? /*#__PURE__*/ jsx(Spinner, {
        size: spinnerSize
    }) : isIconName(iconName) ? /*#__PURE__*/ jsx(HorizonIcon, {
        className: iconSlotClasses,
        name: iconName,
        size: iconSize
    }) : /*#__PURE__*/ jsx(CdnIcon, {
        className: iconSlotClasses,
        iconName: iconName,
        size: iconSize
    });
    const handlePressStart = (event)=>{
        if (!onPointerDown) return;
        const eventType = event.type;
        const pointerType = 'pointerType' in event ? event.pointerType : void 0;
        const now = Date.now();
        const lastPressStart = lastPressStartRef.current;
        const timeSinceLastPressStart = lastPressStart ? now - lastPressStart.timestamp : 1 / 0;
        if (lastPressStart && timeSinceLastPressStart < pressStartDedupeMs) return;
        const isCompatibilityMouseEvent = 'mousedown' === eventType && lastPressStart && timeSinceLastPressStart < compatibilityMouseDedupeMs && ('touchstart' === lastPressStart.eventType || 'pointerdown' === lastPressStart.eventType && 'mouse' !== lastPressStart.pointerType);
        if (isCompatibilityMouseEvent) return;
        lastPressStartRef.current = {
            eventType,
            pointerType,
            timestamp: now
        };
        onPointerDown(event);
    };
    return /*#__PURE__*/ jsxs("button", {
        type: "button",
        onClick: onClick,
        onPointerDown: handlePressStart,
        onMouseDown: handlePressStart,
        onTouchStart: handlePressStart,
        title: title ?? iconName,
        "aria-label": props['aria-label'],
        ...cleanProps(props, [
            'type',
            'shadow',
            'active',
            'loading'
        ]),
        "aria-busy": loading,
        disabled: props.disabled || loading,
        className: buttonClasses,
        ref: ref,
        children: [
            /*#__PURE__*/ jsx("div", {
                className: tapSpaceClasses
            }),
            /*#__PURE__*/ jsx("div", {
                className: wrapperClasses,
                "data-testid": "icon-button-wrapper",
                children: iconContent
            })
        ]
    });
});
IconButton_IconButton.displayName = 'IconButton';
export { IconButton_IconButton as IconButton };
